import React, { useCallback, useEffect, useState } from 'react'
import Cookie from 'js-cookie'
import { currentTimeInMs } from 'src/utils/date'
import modalController from 'src/decorators/ModalController'
import PreviewModeUISync from './PreviewModeUISync'
import MarketingPopupModal from './MarketingPopupModal'
import NewsletterSignupModal from './NewsletterSignupModal'
import Timer from './Timer'
import PageScrollTracker from './PageScrollTracker'
import ExitIntentTrigger from './ExitIntentTrigger'
import { selectSections } from 'src/redux/customisation'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'

const popupFrequencyInDays = {
  once: Infinity,
  daily: 1,
  weekly: 7,
  monthly: 30
}

interface MarketingPopupSection {
  id: string
  name: string
  type: string
  settings: {
    showPopupImage: boolean
    triggerType: string
    timeDelayTrigger: number
    pageScrollTrigger: number
    popupFrequency: string
    headerText: string
    bodyText: string
    closeButtonText: string
    enableNewsletterSignup: boolean
    newsletterHeaderText: string
    newsletterBodyText: string
    newsletterCloseButtonText: string
    popupImage: string
    enableMarketingPopup: boolean
    requestSubscriberName: boolean
  }
  blocks?: {
    [key: string]: unknown
  }
  blocksOrder?: string[]
  updatedAt: string
}

interface MarketingPopupProps {
  id: string
  previewMode: boolean
}

const MarketingPopup = ({
  id,
  previewMode
}: MarketingPopupProps): JSX.Element => {
  const { settings, updatedAt } = useAppSelector(selectSections)[
    id
  ] as MarketingPopupSection
  const {
    menu: { id: menuId }
  } = useAppSelector(selectDetailMenuSite)
  const [triggerEnabled, setTriggerEnable] = useState(true)
  const getLastShownCookieExpiry = () => {
    return popupFrequencyInDays[settings.popupFrequency]
  }

  const resetCookieOnSettingsUpdate = useCallback(() => {
    if (
      parseInt(updatedAt) !== parseInt(Cookie.get('marketingPopupLastUpdate'))
    ) {
      Cookie.set('marketingPopupLastUpdate', updatedAt, { expires: Infinity })
      Cookie.remove('marketingPopupLastShown')
    }
  }, [updatedAt])

  const toggleMarketingPopupTrigger = ({ triggerEnabled }) =>
    setTriggerEnable(triggerEnabled)

  const showMarketingPopupModal = (): void => {
    if (!settings.enableMarketingPopup) return
    if (
      previewMode &&
      (modalController.isOpen('NewsletterSignupModal') || !triggerEnabled)
    ) {
      return
    }
    if (!Cookie.get('marketingPopupLastShown')) {
      modalController.showModal('MarketingPopupModal')
      Cookie.set('marketingPopupLastShown', currentTimeInMs(), {
        expires: getLastShownCookieExpiry()
      })
    }
  }
  const { triggerType, timeDelayTrigger, pageScrollTrigger } = settings

  useEffect(() => {
    resetCookieOnSettingsUpdate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (previewMode) {
      resetCookieOnSettingsUpdate()
    }
  }, [previewMode, resetCookieOnSettingsUpdate])

  return (
    <React.Fragment>
      <PreviewModeUISync
        sectionId={menuId}
        previewMode={previewMode}
        settings={settings}
        toggleMarketingPopupTrigger={toggleMarketingPopupTrigger}
      />
      <MarketingPopupModal
        settings={settings}
        previewMode={previewMode}
      />
      <NewsletterSignupModal settings={settings} />
      {triggerType === 'timeDelay' && (
        <Timer
          onTimerExpired={showMarketingPopupModal}
          duration={timeDelayTrigger}
        />
      )}
      {triggerType === 'pageScroll' && (
        <PageScrollTracker
          onScrollThresholdPassed={showMarketingPopupModal}
          percentageThreshold={pageScrollTrigger}
        />
      )}
      {triggerType === 'exitIntent' && (
        <ExitIntentTrigger onExitIntentTrigger={showMarketingPopupModal} />
      )}
    </React.Fragment>
  )
}

export default MarketingPopup
